import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Minus, Trash2, ChevronLeft, ChevronRight, X, Calendar, Archive, ArchiveRestore, Edit2, PiggyBank } from 'lucide-react';
import { useBudget } from '../../context/BudgetContext';
import { formatCurrency } from '../../utils/formatting';
import {
  annualBudgetSummary, resolveBudgetAmount,
  getPlannedMonthlySavings, getPlannedAnnualSavings, bucketActiveMonthsInYear,
} from '../../utils/calculations';
import { MONTH_NAMES, MONTH_SHORT } from '../../utils/constants';
import { ConfirmDialog, CardList, Modal, BudgetIcon, EmojiPickerModal } from '../Common/Common';
import { AddBucketModal } from '../Savings/Savings';
import CategoryDetailModal from './CategoryDetailModal';
import { ensureRatesForRange, getLatestRate } from '../../utils/exchangeRates';
import { getCurrencyInfo } from '../../utils/currencies';
import { useIsPhone } from '../../hooks/useIsPhone';

/* ─── helpers ─── */
function toMonthStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
function shiftMonth(str, n) {
  const [y, m] = str.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return toMonthStr(d);
}
function monthLabel(str) {
  const [y, m] = str.split('-').map(Number);
  // ponytail: short month keeps the nav button within its 140px minWidth so the
  // totals row never reflows onto a second line for longer-named months.
  return `${MONTH_SHORT[m - 1]} ${y}`;
}

/* ─── Month Picker ─── */
function MonthPicker({ value, onChange, onClose }) {
  const [pickerYear, setPickerYear] = useState(() => parseInt(value.split('-')[0]));
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
      <div style={{
        position: 'absolute', zIndex: 100, top: '100%', left: '50%', transform: 'translateX(-50%)',
        background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10,
        padding: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: 224, marginTop: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setPickerYear(y => y - 1)}>
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{pickerYear}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setPickerYear(y => y + 1)}>
            <ChevronRight size={14} />
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {MONTH_SHORT.map((mn, i) => {
            const ms = `${pickerYear}-${String(i + 1).padStart(2, '0')}`;
            const sel = ms === value;
            return (
              <button key={i} onClick={() => { onChange(ms); onClose(); }} style={{
                padding: '7px 4px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                fontWeight: sel ? 600 : 400,
                border: sel ? '2px solid var(--savings)' : '1px solid var(--border)',
                background: sel ? 'var(--savings-bg)' : 'transparent',
                color: sel ? 'var(--savings)' : 'var(--text-primary)',
              }}>
                {mn}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ─── Main component ─── */
function BudgetPlanner() {
  const {
    categories, budgetItems, yearlyExpenses, transactions, buckets,
    addCategory, deleteCategory, updateCategory, updateBudgetItem, setDefaultFrom, clearBudgetOverride,
    addYearlyExpense, updateYearlyExpense, deleteYearlyExpense,
    addBucket, updateBucket, deleteBucket,
    settings, showToast, theme,
  } = useBudget();
  const mainCurrency = settings.currency || 'USD';
  const [ratesTick, setRatesTick] = useState(0);

  // Prefetch FX rates so foreign-currency buckets convert correctly in the
  // summary/chart. Same pattern as Dashboard.
  useEffect(() => {
    const foreign = [...new Set((buckets || []).map(b => b.currency).filter(c => c && c !== mainCurrency))];
    if (foreign.length === 0) return;
    const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    let cancelled = false;
    Promise.all(foreign.map(c => ensureRatesForRange(c, mainCurrency, ym(start), ym(now)).catch(() => {})))
      .then(() => { if (!cancelled) setRatesTick(t => t + 1); });
    return () => { cancelled = true; };
  }, [buckets, mainCurrency]);

  const fmt = v => formatCurrency(v, settings);
  const fmtRounded = v => {
    try {
      return new Intl.NumberFormat(settings.locale, {
        style: 'currency', currency: settings.currency,
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      }).format(Math.round(v));
    } catch {
      return `${settings.currencySymbol}${Math.round(v)}`;
    }
  };

  const todayMonth = toMonthStr(new Date());
  const [selectedMonth, setSelectedMonth] = useState(todayMonth);
  const [showPicker, setShowPicker] = useState(false);
  const [newCat, setNewCat] = useState({ name: '' });
  const [newYearly, setNewYearly] = useState({ name: '', amount: '', month: '', annual: true, fromYear: '', toYear: '', notes: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);  // {id, name, kind:'cat'|'yearly'}
  const [showArchivedYearly, setShowArchivedYearly] = useState(false);
  const [showArchivedIncome, setShowArchivedIncome] = useState(false);
  const [showArchivedExpense, setShowArchivedExpense] = useState(false);
  const [editingYearly, setEditingYearly] = useState(null);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddYearly, setShowAddYearly] = useState(false);
  const [incomeDeleteMode, setIncomeDeleteMode] = useState(false);
  const [expenseDeleteMode, setExpenseDeleteMode] = useState(false);
  const [yearlyDeleteMode, setYearlyDeleteMode] = useState(false);
  const [editingCatName, setEditingCatName] = useState(null);   // { id, value }
  const [editingYearlyName, setEditingYearlyName] = useState(null); // { id, value }
  const isPhone = useIsPhone();
  // Phone tap-to-edit sheets (desktop uses inline editors instead)
  const [catEdit, setCatEdit] = useState(null);     // category being edited on phone
  const [yearlyEdit, setYearlyEdit] = useState(null); // yearly expense being edited on phone
  const [catDetail, setCatDetail] = useState(null);   // category analytics/detail view (both platforms)
  const [iconEdit, setIconEdit] = useState(null);     // { kind:'cat'|'yearly', id, current } → emoji picker
  const budgetStep = settings.budgetStep || 100;

  const isFuture = selectedMonth > todayMonth;

  /* budget helpers */
  function getEffectiveBudget(id) {
    return resolveBudgetAmount(budgetItems.find(x => x.id === id), selectedMonth);
  }
  function getBase(id) {
    return resolveBudgetAmount(budgetItems.find(x => x.id === id), selectedMonth, { includeOverride: false });
  }
  function hasOverride(id) {
    const b = budgetItems.find(x => x.id === id);
    return !!(b && b.overrides && b.overrides[selectedMonth] !== undefined);
  }
  const currentYear = parseInt(selectedMonth.slice(0, 4));

  // Determine if a yearly expense is active for the given year
  function isActiveYearly(y, year) {
    const fromOk = !y.fromYear || parseInt(y.fromYear) <= year;
    if (y.toYear !== undefined && y.toYear !== null) return fromOk && parseInt(y.toYear) >= year;
    if (y.archiveYear !== undefined && y.archiveYear !== null) return fromOk && parseInt(y.archiveYear) > year;
    return fromOk;
  }

  // Build a two-level index once per transactions change: O(n) → O(1) per lookup
  const txIndex = useMemo(() => {
    const byMonth = {}; // `${catId}:${type}:YYYY-MM` → sum
    const byYear  = {}; // `${catId}:${type}:YYYY`    → sum
    transactions.forEach(t => {
      const mKey = `${t.category}:${t.type}:${t.date.slice(0, 7)}`;
      const yKey = `${t.category}:${t.type}:${t.date.slice(0, 4)}`;
      byMonth[mKey] = (byMonth[mKey] || 0) + t.amount;
      byYear[yKey]  = (byYear[yKey]  || 0) + t.amount;
    });
    return { byMonth, byYear };
  }, [transactions]);

  function getActual(catId, type) {
    return txIndex.byMonth[`${catId}:${type}:${selectedMonth}`] || 0;
  }

  function getActualYear(catId, type) {
    return txIndex.byYear[`${catId}:${type}:${String(currentYear)}`] || 0;
  }

  const incomeCategories   = categories.filter(c => c.type === 'income'  && !c.archived);
  const expenseCategories  = categories.filter(c => c.type === 'expense' && !c.archived);
  const archivedIncome     = categories.filter(c => c.type === 'income'  && c.archived);
  const archivedExpense    = categories.filter(c => c.type === 'expense' && c.archived);

  // Track which ids have ever appeared in a transaction
  const usedIds = useMemo(() => new Set(transactions.map(t => t.category)), [transactions]);
  function isUsed(id) { return usedIds.has(id); }

  // Only include yearly expenses that are active for the current year
  const activeYearly   = yearlyExpenses.filter(y =>  isActiveYearly(y, currentYear));
  const archivedYearly = yearlyExpenses.filter(y => !isActiveYearly(y, currentYear));

  // Annual summary uses only active yearly expenses for the viewed year
  const summary = annualBudgetSummary(categories, budgetItems, activeYearly, currentYear);

  const totalBudgetIncome   = incomeCategories.reduce((s, c) => s + getEffectiveBudget(c.id), 0);
  // Include yearly expenses' monthly provision (annual ÷ 12) in the monthly expense total
  const totalBudgetExpenses =
    expenseCategories.reduce((s, c) => s + getEffectiveBudget(c.id), 0)
    + activeYearly.reduce((s, y) => s + (y.amount || 0) / 12, 0);
  // Always compute actuals (future months will naturally return 0 if nothing logged yet)
  const totalActualIncome   = incomeCategories.reduce((s, c) => s + getActual(c.id, 'income'), 0);
  // Totals use month-scoped actuals so the header matches the monthly budget column
  const totalActualExpenses =
    expenseCategories.reduce((s, c) => s + getActual(c.id, 'expense'), 0)
    + activeYearly.reduce((s, y) => s + getActual(y.id, 'expense'), 0);

  /* ─── Savings plan ─── */
  const activeBuckets = (buckets || []).filter(b => !b.archived);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const plannedMonthlySavings = useMemo(
    () => getPlannedMonthlySavings(buckets, mainCurrency),
    [buckets, mainCurrency, ratesTick]
  );

  // Per-bucket actual savings for the selected month, in the bucket's own currency.
  // Mirrors txnContributionToBucket's logic without exporting it.
  const bucketActualThisMonth = useMemo(() => {
    const map = {};
    activeBuckets.forEach(b => { map[b.id] = 0; });
    transactions.forEach(t => {
      if (t.type !== 'savings' || !t.bucket) return;
      if (!t.date || t.date.slice(0, 7) !== selectedMonth) return;
      if (!Object.prototype.hasOwnProperty.call(map, t.bucket)) return;
      const b = activeBuckets.find(x => x.id === t.bucket);
      let v;
      if (!b.currency) v = t.amount;
      else if (t.currency && t.currency === b.currency && Number.isFinite(t.foreignAmount)) v = t.foreignAmount;
      else v = t.amount;
      map[t.bucket] += Number(v) || 0;
    });
    return map;
  }, [activeBuckets, transactions, selectedMonth]);

  // Sum of monthly bucket actuals converted to main currency, for the totals row.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totalActualSavings = useMemo(() =>
    activeBuckets.reduce((s, b) => {
      const v = bucketActualThisMonth[b.id] || 0;
      if (!v) return s;
      if (!b.currency || b.currency === mainCurrency) return s + v;
      const r = getLatestRate(b.currency, mainCurrency);
      return r != null ? s + v * r : s;
    }, 0),
    [activeBuckets, bucketActualThisMonth, mainCurrency, ratesTick]
  );

  /* ─── Leftover cash ─── */
  // Monthly leftover excludes yearly expenses (per spec — they're amortized in
  // the expense card already, but the user wants the "what's free this month
  // for daily life" number, not the annual provisioning).
  const monthlyExpenseExYearly =
    expenseCategories.reduce((s, c) => s + getEffectiveBudget(c.id), 0);
  const monthlyLeftover = totalBudgetIncome - monthlyExpenseExYearly - plannedMonthlySavings;
  // Annual planned savings — effective, accounts for when each bucket was created
  // so a mid-year bucket only plans for the months it's actually been around.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const plannedAnnualSavings = useMemo(
    () => getPlannedAnnualSavings(buckets, currentYear, mainCurrency),
    [buckets, currentYear, mainCurrency, ratesTick]
  );
  const annualLeftover = summary.income - summary.expenses - plannedAnnualSavings;

  /* ─── Summary tooltip breakdowns ─── */
  // Resolve a bucket's monthly target into the app's main currency, mirroring
  // getPlannedMonthlySavings' conversion logic.
  function bucketMonthlyMain(b) {
    const t = Number(b.monthlyTarget) || 0;
    if (!t) return 0;
    if (!b.currency || b.currency === mainCurrency) return t;
    const r = getLatestRate(b.currency, mainCurrency);
    return r != null ? t * r : 0;
  }

  const monthlyIncomeBreakdown = (() => {
    const rows = incomeCategories
      .filter(c => getEffectiveBudget(c.id) > 0)
      .map(c => `  ${c.name}: ${fmt(getEffectiveBudget(c.id))}`);
    return `Monthly income — ${monthLabel(selectedMonth)}\n${rows.join('\n') || '  (no income set)'}\n──────\nTotal: ${fmt(totalBudgetIncome)}`;
  })();

  const monthlyExpenseBreakdown = (() => {
    const rows = expenseCategories
      .filter(c => getEffectiveBudget(c.id) > 0)
      .map(c => `  ${c.name}: ${fmt(getEffectiveBudget(c.id))}`);
    return `Monthly expenses — ${monthLabel(selectedMonth)}\n(yearly expenses excluded)\n${rows.join('\n') || '  (no expenses set)'}\n──────\nTotal: ${fmt(monthlyExpenseExYearly)}`;
  })();

  const plannedSavingsBreakdown = (() => {
    const rows = activeBuckets
      .filter(b => Number(b.monthlyTarget) > 0)
      .map(b => {
        const main = bucketMonthlyMain(b);
        const native = Number(b.monthlyTarget) || 0;
        const isForeign = b.currency && b.currency !== mainCurrency;
        return isForeign
          ? `  ${b.name}: ${fmt(main)}  (${native.toFixed(2)} ${b.currency})`
          : `  ${b.name}: ${fmt(main)}`;
      });
    return `Planned monthly savings\n${rows.join('\n') || '  (no buckets with a monthly target)'}\n──────\nTotal: ${fmt(plannedMonthlySavings)}`;
  })();

  const monthlyLeftoverBreakdown =
    `Monthly leftover\n  Income:        ${fmt(totalBudgetIncome)}\n− Expenses:    ${fmt(monthlyExpenseExYearly)}\n− Planned savings: ${fmt(plannedMonthlySavings)}\n──────\n= ${fmt(monthlyLeftover)}\n\nNote: yearly expenses are excluded — they\u2019re provisioned annually, not from each month\u2019s leftover.`;

  const annualIncomeBreakdown = (() => {
    const rows = incomeCategories.map(c => {
      const months = Array.from({ length: 12 }, (_, i) => `${currentYear}-${String(i + 1).padStart(2, '0')}`);
      const annual = months.reduce((s, m) => s + resolveBudgetAmount(budgetItems.find(x => x.id === c.id), m), 0);
      return annual > 0 ? `  ${c.name}: ${fmt(annual)}` : null;
    }).filter(Boolean);
    return `Annual income — ${currentYear}\n(sum of each month\u2019s budget across the year)\n${rows.join('\n') || '  (no income set)'}\n──────\nTotal: ${fmt(summary.income)}`;
  })();

  const annualExpensesBreakdown = (() => {
    const monthlyRows = expenseCategories.map(c => {
      const months = Array.from({ length: 12 }, (_, i) => `${currentYear}-${String(i + 1).padStart(2, '0')}`);
      const annual = months.reduce((s, m) => s + resolveBudgetAmount(budgetItems.find(x => x.id === c.id), m), 0);
      return annual > 0 ? `  ${c.name}: ${fmt(annual)}` : null;
    }).filter(Boolean);
    const yearlyRows = activeYearly
      .filter(y => (y.amount || 0) > 0)
      .map(y => `  ${y.name}: ${fmt(y.amount || 0)}`);
    return `Annual expenses — ${currentYear}\nMonthly categories \u00d712:\n${monthlyRows.join('\n') || '  (none)'}\n\nYearly expenses:\n${yearlyRows.join('\n') || '  (none)'}\n──────\nTotal: ${fmt(summary.expenses)}`;
  })();

  const annualSavingsBreakdown = (() => {
    const rows = activeBuckets
      .filter(b => Number(b.monthlyTarget) > 0)
      .map(b => {
        const main = bucketMonthlyMain(b);
        const native = Number(b.monthlyTarget) || 0;
        const isForeign = b.currency && b.currency !== mainCurrency;
        const months = bucketActiveMonthsInYear(b, currentYear);
        const annual = main * months;
        const monthsNote = months === 12 ? '\u00d712' : `\u00d7${months} (active months in ${currentYear})`;
        return isForeign
          ? `  ${b.name}: ${fmt(annual)}  (${(native * months).toFixed(2)} ${b.currency}, ${monthsNote})`
          : `  ${b.name}: ${fmt(annual)}  (${monthsNote})`;
      });
    return `Planned annual savings — ${currentYear}\n(monthly target \u00d7 active months in the year)\n${rows.join('\n') || '  (no buckets with a monthly target)'}\n──────\nTotal: ${fmt(plannedAnnualSavings)}`;
  })();

  const annualLeftoverBreakdown =
    `Annual leftover — ${currentYear}\n  Income:        ${fmt(summary.income)}\n− Expenses (incl. yearly): ${fmt(summary.expenses)}\n− Planned savings (effective): ${fmt(plannedAnnualSavings)}\n──────\n= ${fmt(annualLeftover)}\n\nPlanned savings reflect each bucket\u2019s monthly target \u00d7 the months it was active this year (a mid-year bucket only counts for its actual contribution window).`;

  /* ─── Bucket modal ─── */
  const [bucketModal, setBucketModal] = useState(null); // { mode: 'add' | 'edit', initial?: bucket }
  const [summaryTab, setSummaryTab]   = useState('monthly');

  function handleSaveBucket(data) {
    if (bucketModal?.mode === 'edit' && bucketModal.initial) {
      updateBucket(bucketModal.initial.id, data);
      showToast('Bucket updated');
    } else {
      addBucket(data);
      showToast('Bucket added');
    }
    setBucketModal(null);
  }

  function handleAddCategory(type) {
    if (!newCat.name.trim()) { showToast('Category name is required', 'error'); return; }
    addCategory({ name: newCat.name.trim(), type });
    setNewCat({ name: '' });
    if (type === 'income') setShowAddIncome(false);
    else setShowAddExpense(false);
    showToast('Category added');
  }

  function handleArchiveCategory(id) {
    updateCategory(id, { archived: true });
    showToast('Category archived');
  }

  function handleRestoreCategory(id) {
    updateCategory(id, { archived: false });
    showToast('Category restored');
  }

  function saveCatName(id) {
    if (!editingCatName || editingCatName.id !== id) return;
    const v = editingCatName.value.trim();
    if (v) updateCategory(id, { name: v });
    setEditingCatName(null);
  }

  function saveYearlyName(id) {
    if (!editingYearlyName || editingYearlyName.id !== id) return;
    const v = editingYearlyName.value.trim();
    if (v) updateYearlyExpense(id, { name: v });
    setEditingYearlyName(null);
  }
  function handleAddYearly() {
    if (!newYearly.name.trim()) { showToast('Name is required', 'error'); return; }
    const data = {
      name: newYearly.name,
      amount: parseFloat(newYearly.amount) || 0,
      month: newYearly.month || null,
      notes: newYearly.notes,
    };
    if (newYearly.annual) {
      data.fromYear = currentYear;
    } else {
      if (newYearly.fromYear) data.fromYear = parseInt(newYearly.fromYear);
      if (newYearly.toYear)   data.toYear   = parseInt(newYearly.toYear);
    }
    addYearlyExpense(data);
    setNewYearly({ name: '', amount: '', month: '', annual: true, fromYear: '', toYear: '', notes: '' });
    setShowAddYearly(false);
    showToast('Yearly expense added');
  }

  /* ─── Phone card-list config (tap = edit sheet, swipe-left = delete, swipe-right = archive) ───
   * The % progress lives in a single full-width line pinned to the bottom of each card
   * (the `footer` slot). The exact percentage + spent/budget is shown in the edit sheet on tap. */
  function fullBar(pct, color) {
    return (
      <div className="list-row-bar">
        <div className="list-row-bar-fill" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
      </div>
    );
  }
  // Shared metrics for a budget category (reused by the row footer bar and the edit sheet).
  function catMetrics(c, type) {
    const budget = getEffectiveBudget(c.id);
    const actual = getActual(c.id, type);
    const hasData = actual !== null && budget > 0;
    const pct = hasData ? Math.min(120, Math.round((actual / budget) * 100)) : 0;
    const isIncome = type === 'income';
    const color = isIncome
      ? (actual >= budget ? 'var(--income)' : pct >= 60 ? 'var(--savings)' : 'var(--warning)')
      : (actual > budget ? 'var(--expense)' : pct >= 85 ? 'var(--warning)' : 'var(--income)');
    return { budget, actual, hasData, pct, color };
  }
  const catColumns = type => [
    { slot: 'leading', render: c => (
        <BudgetIcon emoji={c.emoji} color={type === 'income' ? 'var(--income)' : 'var(--expense)'}
          onClick={() => setIconEdit({ kind: 'cat', id: c.id, current: c.emoji })} />
      ) },
    { slot: 'title', render: c => c.name },
    { slot: 'value', render: c => fmt(getEffectiveBudget(c.id)), valueClass: () => (type === 'income' ? 'income-text' : 'expense-text') },
    { slot: 'footer', render: c => { const m = catMetrics(c, type); return m.hasData ? fullBar(m.pct, m.color) : null; } },
  ];
  // Edit = swipe → (L→R, slideRight); Archive + Delete = swipe ← (R→L, slideLeft). Delete warns.
  const catSlideRight = type => [{ key: 'edit', label: 'Edit', icon: <Edit2 size={16} />, color: 'edit', onAction: c => setCatEdit({ ...c, type }) }];
  const catSlideLeft = [
    { key: 'archive', label: 'Archive', icon: <Archive size={16} />, color: 'warning', onAction: c => handleArchiveCategory(c.id) },
    { key: 'delete', label: 'Delete', icon: <Trash2 size={16} />, color: 'danger',
      onAction: c => isUsed(c.id)
        ? showToast('Cannot delete — has transactions. Archive instead.', 'error')
        : setConfirmDelete({ id: c.id, name: c.name, kind: 'cat' }) },
  ];

  const savingsColumns = [
    { slot: 'leading', render: b => <BudgetIcon emoji={b.emoji} color="var(--savings)" /> },
    { slot: 'title',   render: b => b.name },
    { slot: 'value',   render: b => { const code = b.currency || mainCurrency; const symbol = getCurrencyInfo(code)?.symbol || code; return b.monthlyTarget ? `${symbol}${b.monthlyTarget}` : '—'; }, valueClass: () => 'savings-text' },
    { slot: 'footer',  render: b => {
        const target = b.monthlyTarget || 0;
        if (target <= 0) return null;
        const actual = bucketActualThisMonth[b.id] || 0;
        const pct = Math.round((actual / target) * 100);
        return fullBar(pct, actual >= target ? 'var(--income)' : 'var(--savings)');
      } },
  ];
  const savingsSlideRight = [{ key: 'edit', label: 'Edit', icon: <Edit2 size={16} />, color: 'edit', onAction: b => setBucketModal({ mode: 'edit', initial: b }) }];
  const savingsSlideLeft = [
    { key: 'archive', label: 'Archive', icon: <Archive size={16} />, color: 'warning', onAction: b => { updateBucket(b.id, { archived: true }); showToast('Bucket archived'); } },
    { key: 'delete', label: 'Delete', icon: <Trash2 size={16} />, color: 'danger',
      confirm: { title: 'Delete bucket', message: 'Delete this bucket and its adjustment history? Savings transactions are kept. This cannot be undone.', danger: true },
      onAction: b => { deleteBucket(b.id); showToast('Bucket deleted'); } },
  ];

  const yearlyColumns = [
    { slot: 'leading', render: y => (
        <BudgetIcon emoji={y.emoji} color="var(--expense)"
          onClick={() => setIconEdit({ kind: 'yearly', id: y.id, current: y.emoji })} />
      ) },
    { slot: 'title', render: y => y.name },
    { slot: 'sub',   render: y => {
        const fromY = y.fromYear ? parseInt(y.fromYear) : null;
        const toY   = y.toYear ? parseInt(y.toYear) : null;
        const range = fromY && toY ? `${fromY}–${toY}` : fromY ? `from ${fromY}` : toY ? `until ${toY}` : null;
        const monthName = y.month ? MONTH_NAMES[parseInt(y.month) - 1]?.slice(0, 3) : null;
        const meta = [range, monthName].filter(Boolean).join(' · ');
        return <span className="muted">{meta || `${fmtRounded((y.amount || 0) / 12)}/mo`}</span>;
      } },
    { slot: 'value', render: y => fmt(y.amount || 0), valueClass: () => 'expense-text' },
    { slot: 'footer', render: y => {
        const actualYTD = getActualYear(y.id, 'expense');
        if (!(y.amount > 0 && actualYTD > 0)) return null;
        const over = actualYTD > y.amount;
        const pct = Math.min(100, Math.round((actualYTD / y.amount) * 100));
        return fullBar(pct, over ? 'var(--expense)' : pct >= 85 ? 'var(--warning)' : 'var(--income)');
      } },
  ];
  const yearlySlideRight = [{ key: 'edit', label: 'Edit', icon: <Edit2 size={16} />, color: 'edit', onAction: y => setYearlyEdit(y) }];
  const yearlySlideLeft = [
    { key: 'archive', label: 'Archive', icon: <Archive size={16} />, color: 'warning', onAction: y => { updateYearlyExpense(y.id, { toYear: currentYear - 1, archiveYear: undefined }); showToast('Archived'); } },
    { key: 'delete', label: 'Delete', icon: <Trash2 size={16} />, color: 'danger',
      onAction: y => isUsed(y.id)
        ? showToast('Cannot delete — has transactions. Archive instead.', 'error')
        : setConfirmDelete({ id: y.id, name: y.name, kind: 'yearly' }) },
  ];

  return (
    <div>
      {/* Summary card with Monthly / Yearly tabs */}
      <div className="card" style={{ marginBottom: 16, background: 'var(--bg-secondary)' }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
          {[
            { id: 'monthly', label: 'Monthly' },
            { id: 'yearly',  label: 'Yearly' },
          ].map(t => (
            <button
              key={t.id}
              className={`btn btn-sm ${summaryTab === t.id ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setSummaryTab(t.id)}
              style={{ fontSize: 12 }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="bp-summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
          {(summaryTab === 'monthly'
            ? [
                { label: 'Monthly income',    val: totalBudgetIncome,        cls: 'income-text',  title: monthlyIncomeBreakdown },
                { label: 'Monthly expenses',  val: monthlyExpenseExYearly,   cls: 'expense-text', title: monthlyExpenseBreakdown },
                { label: 'Planned savings',   val: plannedMonthlySavings,    cls: 'savings-text', title: plannedSavingsBreakdown },
                { label: 'Monthly leftover',  val: monthlyLeftover,          cls: monthlyLeftover >= 0 ? 'income-text' : 'expense-text', title: monthlyLeftoverBreakdown },
              ]
            : [
                { label: 'Annual income',     val: summary.income,            cls: 'income-text',  title: annualIncomeBreakdown },
                { label: 'Annual expenses',   val: summary.expenses,          cls: 'expense-text', title: annualExpensesBreakdown },
                { label: 'Planned savings',   val: plannedAnnualSavings,      cls: 'savings-text', title: annualSavingsBreakdown },
                { label: 'Annual leftover',   val: annualLeftover,            cls: annualLeftover  >= 0 ? 'income-text' : 'expense-text', title: annualLeftoverBreakdown },
              ]
          ).map(item => (
            <div key={item.label} title={item.title}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 4 }}>{item.label}</div>
              <div className={`mono ${item.cls}`} style={{ fontSize: 20, fontWeight: 500 }}>
                {typeof item.val === 'string' ? item.val : fmt(item.val)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Month navigator */}
      <div className="card bp-month-nav" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelectedMonth(s => shiftMonth(s, -1))}>
            <ChevronLeft size={15} />
          </button>

          {/* Clickable month label → picker */}
          <div style={{ position: 'relative' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowPicker(p => !p)}
              style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 5, minWidth: 96, padding: '4px 8px', justifyContent: 'center' }}
            >
              <Calendar size={13} />
              {monthLabel(selectedMonth)}
            </button>
            {showPicker && (
              <MonthPicker
                value={selectedMonth}
                onChange={setSelectedMonth}
                onClose={() => setShowPicker(false)}
              />
            )}
          </div>

          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelectedMonth(s => shiftMonth(s, 1))}>
            <ChevronRight size={15} />
          </button>

          {isFuture
            ? <span style={{ fontSize: 12, color: 'var(--savings)', fontWeight: 500 }}>planned</span>
            : selectedMonth === todayMonth
              ? <span className="muted" style={{ fontSize: 12 }}>this month</span>
              : null}

          {selectedMonth !== todayMonth && (
            <button className="btn btn-ghost btn-sm" onClick={() => setSelectedMonth(todayMonth)}>
              Today
            </button>
          )}

          {/* Month totals */}
          <div className="bp-month-totals" style={{ marginLeft: 'auto', display: 'flex', gap: 20, fontSize: 13, flexWrap: 'wrap' }}>
            <span>
              <span className="muted">Income </span>
              <span className={`mono ${totalActualIncome >= totalBudgetIncome ? 'income-text' : ''}`}>{fmt(totalActualIncome)}</span>
              <span className="muted"> / {fmt(totalBudgetIncome)}</span>
            </span>
            <span>
              <span className="muted">Expenses </span>
              <span className={`mono ${totalActualExpenses > totalBudgetExpenses ? 'expense-text' : 'income-text'}`}>{fmt(totalActualExpenses)}</span>
              <span className="muted"> / {fmt(totalBudgetExpenses)}</span>
            </span>
            <span title="Actual savings this month / planned monthly savings (sum of bucket monthly targets)">
              <span className="muted">Savings </span>
              <span className={`mono ${totalActualSavings >= plannedMonthlySavings && plannedMonthlySavings > 0 ? 'income-text' : ''}`}>{fmt(totalActualSavings)}</span>
              <span className="muted"> / {fmt(plannedMonthlySavings)}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="grid-2">
        {/* Left: Income + Expenses */}
        <div>
          {/* Income */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ marginBottom: showAddIncome ? 12 : 0 }}>
              <span className="card-title">Income</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span className="mono income-text" style={{ fontSize: 12 }}>{fmt(totalBudgetIncome)}/mo</span>
                <button className="btn btn-ghost btn-icon btn-xs" title="Add income source" onClick={() => { setShowAddIncome(s => !s); setShowAddExpense(false); }}><Plus size={13} /></button>
                <button className={`btn btn-ghost btn-icon btn-xs${incomeDeleteMode ? ' btn-active' : ''}`} title={incomeDeleteMode ? 'Back to archive mode' : 'Enable delete'} onClick={() => setIncomeDeleteMode(s => !s)}><Minus size={13} /></button>
              </div>
            </div>
            {showAddIncome && (
              <div className="bp-add-row" style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <input autoFocus value={newCat.name} onChange={e => setNewCat({ name: e.target.value })}
                    placeholder="e.g. Salary" onKeyDown={e => e.key === 'Enter' && handleAddCategory('income')} />
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => handleAddCategory('income')}><Plus size={13} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAddIncome(false)}><X size={13} /></button>
              </div>
            )}
            {isPhone ? (
              <CardList
                rows={incomeCategories}
                rowKey={c => c.id}
                columns={catColumns('income')}
                onTap={c => setCatEdit({ ...c, type: 'income' })}
                slideLeft={catSlideLeft}
                slideRight={catSlideRight('income')}
              />
            ) : incomeCategories.map(c => (
              <CategoryRow
                key={`${selectedMonth}-${c.id}`}
                cat={c} type="income"
                budget={getEffectiveBudget(c.id)}
                base={getBase(c.id)}
                actual={getActual(c.id, 'income')}
                hasOverride={hasOverride(c.id)}
                isFuture={isFuture}
                fmt={fmt}
                step={budgetStep}
                deleteMode={incomeDeleteMode}
                isUsed={isUsed(c.id)}
                editingName={editingCatName?.id === c.id ? editingCatName.value : null}
                onStartEditName={() => setEditingCatName({ id: c.id, value: c.name })}
                onEditName={v => setEditingCatName({ id: c.id, value: v })}
                onSaveName={() => saveCatName(c.id)}
                onCancelName={() => setEditingCatName(null)}
                onUpdateBase={v => setDefaultFrom(c.id, v, selectedMonth)}
                onSetOverride={v => updateBudgetItem(c.id, v, selectedMonth)}
                onClearOverride={() => clearBudgetOverride(c.id, selectedMonth)}
                onArchive={() => handleArchiveCategory(c.id)}
                onDelete={() => setConfirmDelete({ id: c.id, name: c.name, kind: 'cat' })}
                onSetIcon={() => setIconEdit({ kind: 'cat', id: c.id, current: c.emoji })}
              />
            ))}
            {incomeCategories.length === 0 && !showAddIncome && (
              <p className="muted" style={{ fontSize: 13, padding: '4px 0' }}>No income sources yet.</p>
            )}
            {archivedIncome.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, width: '100%', justifyContent: 'center' }}
                  onClick={() => setShowArchivedIncome(s => !s)}>
                  <ArchiveRestore size={12} /> {showArchivedIncome ? 'Hide' : 'Show'} {archivedIncome.length} archived
                </button>
                {showArchivedIncome && archivedIncome.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, padding: '6px 8px', borderRadius: 8, background: 'var(--bg-secondary)', opacity: 0.7 }}>
                    <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)' }}>{c.name}</span>
                    <button className="btn btn-ghost btn-icon btn-xs" title="Restore" onClick={() => handleRestoreCategory(c.id)}><ArchiveRestore size={12} /></button>
                    {!isUsed(c.id) && <button className="btn btn-ghost btn-icon btn-xs btn-danger" title="Delete permanently" onClick={() => setConfirmDelete({ id: c.id, name: c.name, kind: 'cat' })}><Trash2 size={12} /></button>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Expenses */}
          <div className="card">
            <div className="card-header" style={{ marginBottom: showAddExpense ? 12 : 0 }}>
              <span className="card-title">Expenses</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span className="mono expense-text" style={{ fontSize: 12 }}>{fmt(totalBudgetExpenses)}/mo</span>
                <button className="btn btn-ghost btn-icon btn-xs" title="Add expense category" onClick={() => { setShowAddExpense(s => !s); setShowAddIncome(false); }}><Plus size={13} /></button>
                <button className={`btn btn-ghost btn-icon btn-xs${expenseDeleteMode ? ' btn-active' : ''}`} title={expenseDeleteMode ? 'Back to archive mode' : 'Enable delete'} onClick={() => setExpenseDeleteMode(s => !s)}><Minus size={13} /></button>
              </div>
            </div>
            {showAddExpense && (
              <div className="bp-add-row" style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <input autoFocus value={newCat.name} onChange={e => setNewCat({ name: e.target.value })}
                    placeholder="e.g. Groceries" onKeyDown={e => e.key === 'Enter' && handleAddCategory('expense')} />
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => handleAddCategory('expense')}><Plus size={13} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAddExpense(false)}><X size={13} /></button>
              </div>
            )}
            {isPhone ? (
              <CardList
                rows={expenseCategories}
                rowKey={c => c.id}
                columns={catColumns('expense')}
                onTap={c => setCatDetail(c)}
                slideLeft={catSlideLeft}
                slideRight={catSlideRight('expense')}
              />
            ) : expenseCategories.map(c => (
              <CategoryRow
                key={`${selectedMonth}-${c.id}`}
                cat={c} type="expense"
                budget={getEffectiveBudget(c.id)}
                base={getBase(c.id)}
                actual={getActual(c.id, 'expense')}
                hasOverride={hasOverride(c.id)}
                isFuture={isFuture}
                fmt={fmt}
                step={budgetStep}
                deleteMode={expenseDeleteMode}
                isUsed={isUsed(c.id)}
                editingName={editingCatName?.id === c.id ? editingCatName.value : null}
                onStartEditName={() => setEditingCatName({ id: c.id, value: c.name })}
                onEditName={v => setEditingCatName({ id: c.id, value: v })}
                onSaveName={() => saveCatName(c.id)}
                onCancelName={() => setEditingCatName(null)}
                onUpdateBase={v => setDefaultFrom(c.id, v, selectedMonth)}
                onSetOverride={v => updateBudgetItem(c.id, v, selectedMonth)}
                onClearOverride={() => clearBudgetOverride(c.id, selectedMonth)}
                onArchive={() => handleArchiveCategory(c.id)}
                onDelete={() => setConfirmDelete({ id: c.id, name: c.name, kind: 'cat' })}
                onShowDetail={() => setCatDetail(c)}
                onSetIcon={() => setIconEdit({ kind: 'cat', id: c.id, current: c.emoji })}
              />
            ))}
            {expenseCategories.length === 0 && !showAddExpense && (
              <p className="muted" style={{ fontSize: 13, padding: '4px 0' }}>No expense categories yet.</p>
            )}
            {archivedExpense.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, width: '100%', justifyContent: 'center' }}
                  onClick={() => setShowArchivedExpense(s => !s)}>
                  <ArchiveRestore size={12} /> {showArchivedExpense ? 'Hide' : 'Show'} {archivedExpense.length} archived
                </button>
                {showArchivedExpense && archivedExpense.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, padding: '6px 8px', borderRadius: 8, background: 'var(--bg-secondary)', opacity: 0.7 }}>
                    <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)' }}>{c.name}</span>
                    <button className="btn btn-ghost btn-icon btn-xs" title="Restore" onClick={() => handleRestoreCategory(c.id)}><ArchiveRestore size={12} /></button>
                    {!isUsed(c.id) && <button className="btn btn-ghost btn-icon btn-xs btn-danger" title="Delete permanently" onClick={() => setConfirmDelete({ id: c.id, name: c.name, kind: 'cat' })}><Trash2 size={12} /></button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: savings plan + yearly expenses */}
        <div>
          {/* Savings Plan */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <PiggyBank size={14} /> Savings plan
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span className="mono savings-text" style={{ fontSize: 12 }}>{fmt(plannedMonthlySavings)}/mo</span>
                <button
                  className="btn btn-ghost btn-icon btn-xs"
                  title="Add savings bucket"
                  onClick={() => setBucketModal({ mode: 'add' })}
                >
                  <Plus size={13} />
                </button>
              </div>
            </div>

            {activeBuckets.length === 0 ? (
              <p className="muted" style={{ fontSize: 13, padding: '4px 0' }}>
                No savings buckets yet. Click + to create one.
              </p>
            ) : isPhone ? (
              <CardList
                rows={activeBuckets}
                rowKey={b => b.id}
                columns={savingsColumns}
                onTap={b => setBucketModal({ mode: 'edit', initial: b })}
                slideLeft={savingsSlideLeft}
                slideRight={savingsSlideRight}
              />
            ) : (
              activeBuckets.map(b => (
                <SavingsPlanRow
                  key={`${selectedMonth}-${b.id}`}
                  bucket={b}
                  mainCurrency={mainCurrency}
                  monthActual={bucketActualThisMonth[b.id] || 0}
                  step={budgetStep}
                  onUpdateTarget={v => updateBucket(b.id, { monthlyTarget: v })}
                  onEdit={() => setBucketModal({ mode: 'edit', initial: b })}
                />
              ))
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Yearly expenses</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span className="mono expense-text" style={{ fontSize: 12 }}>{fmt(summary.yearlySum)}/yr · {fmtRounded(summary.yearlySum / 12)}/mo</span>
                <button className="btn btn-ghost btn-icon btn-xs" title="Toggle add form" onClick={() => setShowAddYearly(s => !s)}><Plus size={13} /></button>
                <button className={`btn btn-ghost btn-icon btn-xs${yearlyDeleteMode ? ' btn-active' : ''}`} title={yearlyDeleteMode ? 'Back to archive mode' : 'Enable delete'} onClick={() => setYearlyDeleteMode(s => !s)}><Minus size={13} /></button>
              </div>
            </div>

            {showAddYearly && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 2, minWidth: 120 }}>
                  <input autoFocus value={newYearly.name} onChange={e => setNewYearly(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Car insurance"
                    onKeyDown={e => e.key === 'Enter' && handleAddYearly()} />
                </div>
                <div style={{ flex: 1, minWidth: 80 }}>
                  <input type="number" value={newYearly.amount} onChange={e => setNewYearly(p => ({ ...p, amount: e.target.value }))}
                    placeholder="0/yr" min="0" step="0.01"
                    style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13 }}
                    onKeyDown={e => e.key === 'Enter' && handleAddYearly()} />
                </div>
                <button className="btn btn-primary btn-sm" aria-label="Add yearly expense" onClick={handleAddYearly}><Plus size={13} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAddYearly(false)}><X size={13} /></button>
              </div>
            )}

            {activeYearly.length === 0 && archivedYearly.length === 0 && (
              <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>No yearly expenses added.</p>
            )}

            {isPhone ? (
              <CardList
                rows={activeYearly}
                rowKey={y => y.id}
                columns={yearlyColumns}
                onTap={y => setYearlyEdit(y)}
                slideLeft={yearlySlideLeft}
                slideRight={yearlySlideRight}
              />
            ) : activeYearly.map(y => {
              const actualYTD   = getActualYear(y.id, 'expense');
              const monthProv   = (y.amount || 0) / 12;
              const over        = actualYTD > 0 && y.amount > 0 && actualYTD > y.amount;
              const pct         = y.amount > 0 && actualYTD > 0 ? Math.min(100, Math.round((actualYTD / y.amount) * 100)) : null;
              const isOngoing   = !y.toYear;
              const yearPct     = Math.round(((new Date().getMonth() + 1) / 12) * 100);
              const isEditing = editingYearly === y.id;
              const fromY     = y.fromYear ? parseInt(y.fromYear) : null;
              const toY       = y.toYear   ? parseInt(y.toYear)   : null;
              const yearRange = fromY && toY ? `${fromY}–${toY}` : fromY ? `from ${fromY}` : toY ? `until ${toY}` : null;
              return (
                <div key={y.id} style={{ marginBottom: 14 }}>
                  {/* Row 1: name | annual input | YTD actual | archive/delete — matches CategoryRow layout */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BudgetIcon emoji={y.emoji} color="var(--expense)"
                      onClick={() => setIconEdit({ kind: 'yearly', id: y.id, current: y.emoji })} desktop />
                    {editingYearlyName?.id === y.id ? (
                      <input
                        autoFocus
                        value={editingYearlyName.value}
                        onChange={e => setEditingYearlyName({ id: y.id, value: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') saveYearlyName(y.id); if (e.key === 'Escape') setEditingYearlyName(null); }}
                        onBlur={() => saveYearlyName(y.id)}
                        style={{ flex: 1, fontSize: 13 }}
                      />
                    ) : (
                      <span
                        style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}
                        title="Click to rename"
                        onClick={() => setEditingYearlyName({ id: y.id, value: y.name })}
                      >{y.name}</span>
                    )}
                    <input
                      type="number"
                      value={y.amount || ''}
                      onChange={e => updateYearlyExpense(y.id, { amount: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      min="0" step={budgetStep}
                      style={{ width: 100, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13 }}
                      title="Annual amount"
                    />
                    <span className="mono" style={{ width: 90, textAlign: 'right', fontSize: 13, color: over ? 'var(--expense)' : 'var(--income)' }}>
                      {actualYTD > 0 ? <>{fmt(actualYTD)}{over ? ' ⚠' : ''}</> : null}
                    </span>
                    {yearlyDeleteMode && !isUsed(y.id) ? (
                      <button
                        className="btn btn-ghost btn-icon btn-xs btn-danger"
                        title="Delete permanently"
                        onClick={() => setConfirmDelete({ id: y.id, name: y.name, kind: 'yearly' })}
                      >
                        <Trash2 size={12} />
                      </button>
                    ) : (
                      <button
                        className="btn btn-ghost btn-icon btn-xs"
                        title={yearlyDeleteMode ? 'Cannot delete — has transactions' : 'Archive'}
                        onClick={() => !yearlyDeleteMode && (updateYearlyExpense(y.id, { toYear: currentYear - 1, archiveYear: undefined }), showToast('Archived'))}
                        style={yearlyDeleteMode ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                      >
                        <Archive size={12} />
                      </button>
                    )}
                  </div>
                  {/* Row 2: year range | month | /yr label | monthly provision | Edit button */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                    {yearRange && <span className="muted" style={{ fontSize: 11 }}>{yearRange}</span>}
                    {isOngoing && monthProv === 0 && <span className="muted" style={{ fontSize: 11 }}>Until {currentYear + 1}</span>}
                    {y.month && <span className="muted" style={{ fontSize: 11 }}>{MONTH_NAMES[parseInt(y.month) - 1]?.slice(0, 3)}</span>}
                    <span style={{ flex: 1 }} />
                    {monthProv > 0 && <span className="muted" style={{ fontSize: 11 }}>≈ {fmtRounded(monthProv)}/mo</span>}
                    {isOngoing && monthProv === 0 && actualYTD > 0 && (
                      <span className="muted" style={{ fontSize: 11 }}>≈ {fmtRounded(actualYTD / 12)}/mo</span>
                    )}
                    <button
                      className={`btn btn-ghost btn-icon btn-xs${isEditing ? ' btn-active' : ''}`}
                      title={isEditing ? 'Close details' : 'Edit details'}
                      onClick={() => setEditingYearly(isEditing ? null : y.id)}
                      style={{ marginLeft: 2 }}
                    >
                      <Edit2 size={11} />
                    </button>
                  </div>

                  {/* Progress bar: always show for ongoing (full bar when no amount), normal pct otherwise */}
                  {(monthProv > 0 || isOngoing) && (
                    <div style={{ marginTop: 5, height: 3, background: 'var(--bg-tertiary)', borderRadius: 100, overflow: 'hidden' }}>
                      <div style={{ width: `${(isOngoing && monthProv === 0) ? yearPct : Math.min(100, pct ?? 0)}%`, height: '100%', borderRadius: 100, background: over ? 'var(--expense)' : (pct ?? 0) >= 85 ? 'var(--warning)' : 'var(--income)' }} />
                    </div>
                  )}

                  {/* Edit panel */}
                  {isEditing && (
                    <div style={{ marginTop: 10, padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 2 }}>
                          <label>Name</label>
                          <input
                            value={y.name}
                            onChange={e => updateYearlyExpense(y.id, { name: e.target.value })}
                            placeholder="e.g. Car insurance"
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label>Due month</label>
                          <select
                            value={y.month || ''}
                            onChange={e => updateYearlyExpense(y.id, { month: e.target.value || null })}
                          >
                            <option value="">— none —</option>
                            {MONTH_NAMES.map((mn, i) => (
                              <option key={i} value={String(i + 1).padStart(2, '0')}>{mn}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <label>From year</label>
                          <input
                            key={`from-${y.id}-${y.fromYear || ''}`}
                            type="number"
                            defaultValue={y.fromYear || ''}
                            onBlur={e => {
                              const v = e.target.value ? parseInt(e.target.value) : undefined;
                              if (v !== (y.fromYear || undefined)) updateYearlyExpense(y.id, { fromYear: v });
                            }}
                            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            placeholder="—"
                            min="2000" max="2100"
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label>To year</label>
                          <input
                            key={`to-${y.id}-${y.toYear || ''}`}
                            type="number"
                            defaultValue={y.toYear || ''}
                            onBlur={e => {
                              const v = e.target.value ? parseInt(e.target.value) : undefined;
                              if (v !== (y.toYear || undefined)) updateYearlyExpense(y.id, { toYear: v, archiveYear: undefined });
                            }}
                            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            placeholder="ongoing"
                            min="2000" max="2100"
                          />
                        </div>
                      </div>
                      <div>
                        <label>Notes</label>
                        <input
                          value={y.notes || ''}
                          onChange={e => updateYearlyExpense(y.id, { notes: e.target.value })}
                          placeholder="Policy number, provider, etc."
                        />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button
                          className="btn btn-ghost btn-sm btn-danger"
                          onClick={() => { deleteYearlyExpense(y.id); setEditingYearly(null); showToast('Yearly expense deleted'); }}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                        <button className="btn btn-sm" onClick={() => setEditingYearly(null)}>Done</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Archived yearly expenses toggle */}
            {archivedYearly.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowArchivedYearly(s => !s)}
                  style={{ fontSize: 12, width: '100%', justifyContent: 'center' }}
                >
                  <ArchiveRestore size={12} />
                  {showArchivedYearly ? 'Hide' : 'Show'} {archivedYearly.length} archived
                </button>
                {showArchivedYearly && archivedYearly.map(y => (
                  <div key={y.id} style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--bg-secondary)', opacity: 0.7, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)' }}>{y.name}</span>
                    <span className="muted" style={{ fontSize: 11 }}>{fmt(y.amount || 0)}/yr</span>
                    <span className="muted" style={{ fontSize: 11 }}>
                      {y.toYear ? `ended ${y.toYear}` : y.archiveYear ? `from ${y.archiveYear}` : 'archived'}
                    </span>
                    <button
                      className="btn btn-ghost btn-icon btn-xs"
                      title="Restore"
                      onClick={() => { updateYearlyExpense(y.id, { archiveYear: undefined, toYear: undefined }); showToast('Yearly expense restored'); }}
                    >
                      <ArchiveRestore size={12} />
                    </button>
                    <button className="btn btn-ghost btn-icon btn-xs btn-danger" onClick={() => deleteYearlyExpense(y.id)} title="Delete permanently">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>

      {bucketModal && (
        <AddBucketModal
          initial={bucketModal.initial}
          mainCurrency={mainCurrency}
          onSave={handleSaveBucket}
          onClose={() => setBucketModal(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={confirmDelete.kind === 'yearly' ? 'Delete yearly expense' : 'Delete category'}
          message={`Delete "${confirmDelete.name}"? This cannot be undone.`}
          danger
          onConfirm={() => {
            if (confirmDelete.kind === 'yearly') {
              deleteYearlyExpense(confirmDelete.id);
              showToast('Yearly expense deleted');
            } else {
              deleteCategory(confirmDelete.id);
              showToast('Category deleted');
            }
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Phone edit sheets */}
      {catEdit && (
        <CategoryEditSheet
          cat={catEdit}
          base={getBase(catEdit.id)}
          effective={getEffectiveBudget(catEdit.id)}
          hasOverride={hasOverride(catEdit.id)}
          metrics={catMetrics(catEdit, catEdit.type)}
          fmt={fmt}
          onSaveName={name => { if (name.trim()) updateCategory(catEdit.id, { name: name.trim() }); }}
          onSetDefault={v => setDefaultFrom(catEdit.id, v, selectedMonth)}
          onSetOverride={v => updateBudgetItem(catEdit.id, v, selectedMonth)}
          onClearOverride={() => clearBudgetOverride(catEdit.id, selectedMonth)}
          onClose={() => setCatEdit(null)}
        />
      )}
      {yearlyEdit && (
        <YearlyEditSheet
          yearly={yearlyEdit}
          fmt={fmt}
          onSave={updates => updateYearlyExpense(yearlyEdit.id, updates)}
          onClose={() => setYearlyEdit(null)}
        />
      )}
      {catDetail && (
        <CategoryDetailModal
          category={catDetail}
          transactions={transactions}
          budgetItem={budgetItems.find(x => x.id === catDetail.id)}
          currentBudget={getBase(catDetail.id)}
          fmt={fmt}
          settings={settings}
          onApplyBudget={v => { setDefaultFrom(catDetail.id, v, selectedMonth); showToast('Budget updated'); }}
          onEdit={() => { setCatEdit({ ...catDetail, type: 'expense' }); setCatDetail(null); }}
          onClose={() => setCatDetail(null)}
        />
      )}
      {iconEdit && (
        <EmojiPickerModal
          current={iconEdit.current}
          theme={theme}
          onPick={emoji => {
            if (iconEdit.kind === 'yearly') updateYearlyExpense(iconEdit.id, { emoji });
            else updateCategory(iconEdit.id, { emoji });
            setIconEdit(null);
          }}
          onClear={() => {
            if (iconEdit.kind === 'yearly') updateYearlyExpense(iconEdit.id, { emoji: '' });
            else updateCategory(iconEdit.id, { emoji: '' });
            setIconEdit(null);
          }}
          onClose={() => setIconEdit(null)}
        />
      )}
    </div>
  );
}

/* ─── Phone: category edit sheet ─── */
function CategoryEditSheet({ cat, base, effective, hasOverride, metrics, fmt, onSaveName, onSetDefault, onSetOverride, onClearOverride, onClose }) {
  const [name, setName] = useState(cat.name);
  const [amount, setAmount] = useState(String(effective || ''));
  const [thisMonthOnly, setThisMonthOnly] = useState(hasOverride);

  function save() {
    onSaveName(name);
    const v = parseFloat(amount) || 0;
    if (thisMonthOnly) onSetOverride(v);
    else { if (hasOverride) onClearOverride(); onSetDefault(v); }
    onClose();
  }

  const isIncome = cat.type === 'income';

  return (
    <Modal title={`Edit ${isIncome ? 'income' : 'expense'}`} variant="sheet" onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>Save changes</button>
        </>
      }>
      {metrics && metrics.hasData && (
        <div style={{ marginBottom: 14 }}>
          <div className="flex" style={{ justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
            <span className="muted">{isIncome ? 'Received' : 'Spent'} {fmt(metrics.actual)} of {fmt(metrics.budget)}</span>
            <span className="mono" style={{ fontWeight: 600, color: metrics.color }}>{metrics.pct}%</span>
          </div>
          <div className="list-row-bar">
            <div className="list-row-bar-fill" style={{ width: `${Math.min(100, metrics.pct)}%`, background: metrics.color }} />
          </div>
        </div>
      )}
      <div className="form-group">
        <label>Name</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Monthly budget</label>
        <input type="number" min="0" step="50" inputMode="decimal"
          value={amount} onChange={e => setAmount(e.target.value)} />
      </div>
      <label className="flex" style={{ gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={thisMonthOnly} onChange={e => setThisMonthOnly(e.target.checked)} style={{ width: 'auto' }} />
        This month only (otherwise sets the default{base ? `, currently ${fmt(base)}` : ''})
      </label>
    </Modal>
  );
}

/* ─── Phone: yearly-expense edit sheet ─── */
function YearlyEditSheet({ yearly, fmt, onSave, onClose }) {
  const [form, setForm] = useState({
    name: yearly.name || '',
    amount: String(yearly.amount || ''),
    month: yearly.month || '',
    fromYear: yearly.fromYear ? String(yearly.fromYear) : '',
    toYear: yearly.toYear ? String(yearly.toYear) : '',
  });
  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const monthly = (parseFloat(form.amount) || 0) / 12;

  function save() {
    onSave({
      name: form.name.trim() || yearly.name,
      amount: parseFloat(form.amount) || 0,
      month: form.month || null,
      fromYear: form.fromYear ? parseInt(form.fromYear) : undefined,
      toYear: form.toYear ? parseInt(form.toYear) : undefined,
    });
    onClose();
  }

  return (
    <Modal title="Edit yearly expense" variant="sheet" onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>Save changes</button>
        </>
      }>
      <div className="form-group">
        <label>Name</label>
        <input autoFocus value={form.name} onChange={e => upd('name', e.target.value)} />
      </div>
      <div className="form-group">
        <label>Annual amount</label>
        <input type="number" min="0" step="50" inputMode="decimal"
          value={form.amount} onChange={e => upd('amount', e.target.value)} />
        {monthly > 0 && fmt && <div className="muted" style={{ fontSize: 12, marginTop: 5 }}>≈ {fmt(monthly)} / month</div>}
      </div>
      <div className="form-group">
        <label>Due month</label>
        <select value={form.month} onChange={e => upd('month', e.target.value)}>
          <option value="">— none —</option>
          {MONTH_NAMES.map((mn, i) => (
            <option key={i} value={String(i + 1).padStart(2, '0')}>{mn}</option>
          ))}
        </select>
      </div>
      <div className="flex" style={{ gap: 10 }}>
        <div className="form-group" style={{ flex: 1, marginBottom: 6 }}>
          <label>From year</label>
          <input type="number" inputMode="numeric" placeholder="—" value={form.fromYear} onChange={e => upd('fromYear', e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1, marginBottom: 6 }}>
          <label>To year</label>
          <input type="number" inputMode="numeric" placeholder="ongoing" value={form.toYear} onChange={e => upd('toYear', e.target.value)} />
        </div>
      </div>
      <div className="muted" style={{ fontSize: 12 }}>Leave “To year” empty for an ongoing expense.</div>
    </Modal>
  );
}

/* ─── Category row ─── */
function CategoryRow({
  cat, type, budget, base, actual, hasOverride, isFuture, fmt, step = 100,
  onUpdateBase, onSetOverride, onClearOverride,
  deleteMode, isUsed,
  editingName, onStartEditName, onEditName, onSaveName, onCancelName,
  onArchive, onDelete, onShowDetail, onSetIcon,
}) {
  const [pendingVal, setPendingVal] = useState(null);
  const isIncome = type === 'income';

  const displayVal = pendingVal !== null ? pendingVal : budget;
  const pct = budget > 0 && actual !== null ? Math.min(120, Math.round((actual / budget) * 100)) : 0;

  let barColor = 'var(--border-strong)';
  let actualColor = 'var(--text-secondary)';
  if (actual !== null && budget > 0) {
    if (isIncome) {
      barColor = actual >= budget ? 'var(--income)' : pct >= 60 ? 'var(--savings)' : 'var(--warning)';
      actualColor = actual >= budget ? 'var(--income)' : 'var(--text-secondary)';
    } else {
      barColor = actual > budget ? 'var(--expense)' : pct >= 85 ? 'var(--warning)' : 'var(--income)';
      actualColor = actual > budget ? 'var(--expense)' : 'var(--income)';
    }
  }

  const variance = actual !== null && budget > 0 ? actual - budget : null;
  const showBar = actual !== null && budget > 0 && pendingVal === null;

  // Whole-row click opens insights, except on interactive bits (inputs, buttons,
  // selects) or the click-to-rename name (marked with data-no-detail).
  function handleRowClick(e) {
    if (!onShowDetail) return;
    if (e.target.closest('input, button, select, textarea, a, [data-no-detail]')) return;
    onShowDetail();
  }

  return (
    <div
      style={{ marginBottom: 14, ...(onShowDetail ? { cursor: 'pointer' } : null) }}
      onClick={handleRowClick}
      title={onShowDetail ? 'View insights' : undefined}
    >
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {onSetIcon && (
          <BudgetIcon emoji={cat.emoji} color={isIncome ? 'var(--income)' : 'var(--expense)'} onClick={onSetIcon} desktop />
        )}
        {/* Name — click to edit inline */}
        {editingName !== null ? (
          <input
            autoFocus
            value={editingName}
            onChange={e => onEditName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onSaveName(); if (e.key === 'Escape') onCancelName(); }}
            onBlur={onSaveName}
            style={{ flex: 1, fontSize: 13 }}
          />
        ) : (
          <span
            data-no-detail
            style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}
            title="Click to rename"
            onClick={onStartEditName}
          >
            {cat.name}
          </span>
        )}

        {/* Budget input */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="number"
            value={displayVal || ''}
            onChange={e => setPendingVal(parseFloat(e.target.value) || 0)}
            onKeyDown={e => { if (e.key === 'Escape') setPendingVal(null); }}
            placeholder="0.00"
            min="0"
            step={step}
            style={{
              width: 100, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13,
              borderColor: pendingVal !== null
                ? 'var(--savings)'
                : hasOverride ? 'var(--accent)' : undefined,
              paddingRight: (hasOverride && pendingVal === null) ? 24 : undefined,
            }}
          />
          {hasOverride && pendingVal === null && (
            <button
              onClick={onClearOverride}
              title={`Revert to default (${fmt(base)})`}
              style={{
                position: 'absolute', right: 4, background: 'none', border: 'none',
                cursor: 'pointer', padding: 2, color: 'var(--text-tertiary)',
                display: 'flex', alignItems: 'center',
              }}
            >
              <X size={10} />
            </button>
          )}
        </div>

        {/* Actual */}
        {actual !== null && (
          <span className="mono" style={{ width: 90, textAlign: 'right', fontSize: 13, color: actualColor }}>
            {fmt(actual)}
          </span>
        )}

        {/* Archive / Delete button */}
        {deleteMode && !isUsed ? (
          <button className="btn btn-ghost btn-icon btn-xs btn-danger" onClick={onDelete} title="Delete permanently">
            <Trash2 size={12} />
          </button>
        ) : deleteMode && isUsed ? (
          <button className="btn btn-ghost btn-icon btn-xs" title="Cannot delete — has transactions" style={{ opacity: 0.3, cursor: 'not-allowed' }}>
            <Trash2 size={12} />
          </button>
        ) : (
          <button className="btn btn-ghost btn-icon btn-xs" onClick={onArchive} title="Archive">
            <Archive size={12} />
          </button>
        )}
      </div>

      {/* Pending: commit-choice buttons */}
      {pendingVal !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <span className="muted" style={{ fontSize: 11, flex: 1 }}>Apply as:</span>
          <button
            className="btn btn-sm"
            onClick={() => { onSetOverride(pendingVal); setPendingVal(null); }}
            style={{ fontSize: 11, padding: '3px 10px' }}
          >
            This month only
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => { onUpdateBase(pendingVal); setPendingVal(null); }}
            style={{ fontSize: 11, padding: '3px 10px' }}
          >
            Set as default
          </button>
          <button className="btn btn-ghost btn-icon btn-xs" onClick={() => setPendingVal(null)} title="Cancel">
            <X size={12} />
          </button>
        </div>
      )}

      {/* Override note */}
      {hasOverride && pendingVal === null && (
        <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2 }}>
          Custom for this month · default {fmt(base)}
        </div>
      )}

      {/* Future hint */}
      {isFuture && !hasOverride && pendingVal === null && (
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
          Using default · edit to set a specific expectation for this month
        </div>
      )}

      {/* Progress bar + variance */}
      {showBar && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
          <div style={{ flex: 1, height: 3, background: 'var(--bg-tertiary)', borderRadius: 100, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(100, pct)}%`, height: '100%',
              background: barColor, borderRadius: 100, transition: 'width 0.3s ease',
            }} />
          </div>
          <span className="muted" style={{ fontSize: 11, minWidth: 30, textAlign: 'right' }}>{pct}%</span>
          {variance !== null && (
            <span className="mono" style={{
              fontSize: 11, minWidth: 90, textAlign: 'right',
              color: isIncome
                ? (variance >= 0 ? 'var(--income)' : 'var(--expense)')
                : (variance > 0 ? 'var(--expense)' : 'var(--income)'),
            }}>
              {isIncome
                ? (variance >= 0 ? `+${fmt(variance)}` : fmt(variance))
                : (variance <= 0 ? `${fmt(Math.abs(variance))} left` : `+${fmt(variance)} over`)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Savings plan row ─── */
function SavingsPlanRow({ bucket, mainCurrency, monthActual, step, onUpdateTarget, onEdit }) {
  const [pending, setPending] = useState(null);
  const isForeign = bucket.currency && bucket.currency !== mainCurrency;
  const code   = bucket.currency || mainCurrency;
  const symbol = getCurrencyInfo(code)?.symbol || code;
  const target = bucket.monthlyTarget || 0;
  const display = pending !== null ? pending : (target || '');
  const pct = target > 0 ? Math.round((monthActual / target) * 100) : 0;
  const met = target > 0 && monthActual >= target;

  function commit() {
    if (pending === null) return;
    const n = parseFloat(pending) || 0;
    if (n !== (bucket.monthlyTarget || 0)) onUpdateTarget(n);
    setPending(null);
  }

  function fmtNative(v) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: code,
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(v);
    } catch {
      return `${symbol}${(v || 0).toFixed(2)}`;
    }
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <BudgetIcon emoji={bucket.emoji} color="var(--savings)" desktop />
          <span>{bucket.name}</span>
          {isForeign && <span className="muted" style={{ fontSize: 11 }}>· {code}</span>}
        </span>
        <input
          type="number"
          value={display}
          onChange={e => setPending(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter')  e.currentTarget.blur();
            if (e.key === 'Escape') { setPending(null); e.currentTarget.blur(); }
          }}
          placeholder="0.00"
          min="0"
          step={step}
          title={isForeign ? `Monthly target in ${code}` : 'Monthly target'}
          style={{
            width: 100, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13,
            borderColor: pending !== null ? 'var(--savings)' : undefined,
          }}
        />
        <span
          className="mono"
          style={{
            width: 90, textAlign: 'right', fontSize: 13,
            color: monthActual > 0 ? (met ? 'var(--income)' : 'var(--savings)') : 'var(--text-tertiary)',
          }}
          title={monthActual > 0 ? `Actual savings this month` : 'No savings logged this month yet'}
        >
          {monthActual > 0 ? fmtNative(monthActual) : ''}
        </span>
        <button className="btn btn-ghost btn-icon btn-xs" title="Edit bucket" onClick={onEdit}>
          <Edit2 size={12} />
        </button>
      </div>

      {target > 0 && monthActual > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
          <div style={{ flex: 1, height: 3, background: 'var(--bg-tertiary)', borderRadius: 100, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(100, pct)}%`, height: '100%',
              background: met ? 'var(--income)' : pct >= 60 ? 'var(--savings)' : 'var(--warning)',
              borderRadius: 100, transition: 'width 0.3s ease',
            }} />
          </div>
          <span className="muted" style={{ fontSize: 11, minWidth: 30, textAlign: 'right' }}>{pct}%</span>
        </div>
      )}
    </div>
  );
}

export default React.memo(BudgetPlanner);
